import { useCallback, useEffect } from 'react'

import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import ListSubheader from '@mui/material/ListSubheader'
import { SxProps } from '@mui/system'
import useId from '@mui/utils/useId'

export type DropdownProps<T> = {
  className?: string
  label?: string
  showLabel?: boolean
  collection: T[]
  item: T | null | undefined
  setItem: React.Dispatch<React.SetStateAction<T | null>>
  renderItem: (item: T) => React.ReactNode
  selectFirstByDefault?: boolean
  disabled?: boolean
  loading?: boolean
  sx?: SxProps
  variant?: 'flat' | 'outlined'
}

export default function Dropdown<T extends { id: string, type: string }>({
  className,
  label,
  collection,
  item,
  setItem,
  renderItem,
  selectFirstByDefault,
  disabled = false,
  loading = false,
  sx = {},
  variant = 'outlined',
  ...props
} : DropdownProps<T>) {
  const labelId = useId()
  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    setItem(collection.find(i => i.id === event.target.value) ?? null)
  }, [collection, setItem])

  useEffect(() => {
    const itemExists = !!item
    if(selectFirstByDefault && collection && !itemExists) {
      setItem(collection[0])
    }
  }, [item, collection, selectFirstByDefault, setItem])

  const selectVariant = variant === 'outlined' ? variant : 'standard'
  const selectSx = variant === 'outlined' ? 
    {
      minWidth: '174px',
      maxWidth: '251px',
      minHeight: '50px',
      maxHeight: '50px',
      fontSize: '18px',
    } : {
      '&::before': {
        border: 'none !important'
      }
    }

  const fullWidth = variant === 'outlined'

  return (
    <FormControl className={className} fullWidth={fullWidth}>
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={label ? labelId : undefined}
        label={label}
        value={item && item.id || ''}
        onChange={handleChange}
        disabled={disabled}
        variant={selectVariant}
        sx={{
          ...selectSx,
          ...sx,
        }}
        {...props}
      >
        { loading && (
          <MenuItem value="-">
            Please wait...
          </MenuItem>
        )}

        {!loading && collection && collection.map(itemOption => (
          itemOption.type === 'heading' &&
          <ListSubheader key={itemOption.id}>
            { renderItem(itemOption) }
          </ListSubheader>
          ||
          <MenuItem key={itemOption.id} value={itemOption.id}>
            { renderItem(itemOption) }
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
